/**
 * AdminRefundRequests — /admin/refund-requests
 *
 * Lists user-initiated refund requests with approve/deny actions. Refunds
 * are still processed offline (Stripe wiring pending) — approving here
 * cancels the user's active subscription + demotes them to free.
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Wallet, Check, X, RefreshCw } from 'lucide-react';

const STATUSES = ['pending', 'approved', 'denied', 'all'];

const STATUS_VARIANT = {
  pending:  'default',
  approved: 'secondary',
  denied:   'outline',
};

const AdminRefundRequests = () => {
  const { API } = useContext(AuthContext);
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [decidingId, setDecidingId] = useState(null);
  const [note, setNote] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/refund-requests`, { params: { status } });
      setRows(data.requests || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load refund requests.');
    } finally {
      setLoading(false);
    }
  }, [API, status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const decide = async (id, action) => {
    try {
      await axios.post(`${API}/admin/refund-requests/${id}/${action}`, { note });
      toast.success(action === 'approve' ? 'Refund approved — subscription cancelled.' : 'Refund denied.');
      setDecidingId(null);
      setNote('');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${action}.`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="admin-refund-requests-page">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Billing</div>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground leading-tight mt-1">
              Refund requests
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Approve = cancel subscription + demote to free. Actual payment
              refund is handled offline until Stripe is wired.
            </p>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Status filter */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="status-filter">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                status === s
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted/40'
              }`}
              data-testid={`status-${s}-btn`}
            >
              {s} {s !== 'all' && counts[s] ? `(${counts[s]})` : ''}
            </button>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Requests</CardTitle>
            <CardDescription>Newest first · {rows.length} in view</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading && <div className="p-6 text-center text-muted-foreground text-sm">Loading…</div>}
            {!loading && rows.length === 0 && (
              <div className="p-8 text-center text-muted-foreground text-sm" data-testid="refunds-empty">
                No refund requests in this bucket.
              </div>
            )}
            {!loading && rows.map((r) => (
              <div key={r.id} className="p-4 border-b border-border last:border-b-0" data-testid={`refund-row-${r.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={STATUS_VARIANT[r.status] || 'outline'} className="text-[10px] uppercase tracking-widest">
                        {r.status}
                      </Badge>
                      <span className="text-sm font-heading font-extrabold text-foreground truncate">{r.email}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span>Tier: <strong>{r.tier_name || '—'}</strong></span>
                      <span>·</span>
                      <span>{r.billing_cycle || 'unknown'}</span>
                      <span>·</span>
                      <span>{r.consumption_pct_at_request}% consumed</span>
                      <span>·</span>
                      <span>{r.days_remaining_at_request}d left at request</span>
                      <span>·</span>
                      <span>{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                    {r.reason && (
                      <p className="text-xs mt-2 text-foreground/90 italic bg-muted/40 rounded-md p-2 border border-border/60">
                        &ldquo;{r.reason}&rdquo;
                      </p>
                    )}
                    {r.resolution_note && (
                      <p className="text-[11px] mt-2 text-muted-foreground">
                        Resolution by {r.resolved_by} on {new Date(r.resolved_at).toLocaleDateString()}: {r.resolution_note}
                      </p>
                    )}
                  </div>
                  {r.status === 'pending' && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDecidingId(r.id)}
                        data-testid={`decide-${r.id}`}
                      >
                        Decide…
                      </Button>
                    </div>
                  )}
                </div>

                {/* Inline decision panel */}
                {decidingId === r.id && (
                  <div className="mt-3 p-3 rounded-lg bg-muted/50 border border-border" data-testid={`decision-panel-${r.id}`}>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Optional note (visible to the user)"
                      className="w-full text-xs p-2 rounded border border-input bg-background"
                      rows={2}
                      data-testid="decision-note-input"
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <Button size="sm" variant="ghost" onClick={() => { setDecidingId(null); setNote(''); }}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => decide(r.id, 'deny')}
                        data-testid={`deny-${r.id}`}
                      >
                        <X className="w-3.5 h-3.5 mr-1" /> Deny
                      </Button>
                      <Button
                        size="sm"
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                        onClick={() => decide(r.id, 'approve')}
                        data-testid={`approve-${r.id}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminRefundRequests;
